import Cookies from 'js-cookie';
import { MSW_STATUS } from '@legacy/constants/cookies';
import { toCamel } from '@legacy/utils/object';
import {
  GetFetchProps,
  PostFetchProps,
  DeleteFetchProps,
  GenerateUrlProps,
  PutFetchProps,
  PatchFetchProps,
} from './type';
import {
  BusinessRuleViolation,
  BusinessRuleViolationError,
  TlpErrorResponse,
} from '../errors/errorBase';
import { tlpBase } from '@/utils/gateway';
import { userManager } from '@/auth/userManager';

const fetchWithAuth = async (
  url: string,
  init: RequestInit,
  retried = false,
): Promise<Response> => {
  const response = await fetch(url, init);
  if (response.status !== 401 || retried) return response;

  try {
    await userManager.signinSilent();
    return fetchWithAuth(url, init, true);
  } catch {
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
    return response;
  }
};

const responseHandling = async (response: Response) => {
  const { status, statusText, url } = response;

  // Handle 204 for no body response
  if (status === 204) {
    return '';
  }

  const data = await response.json();

  if (response.ok) {
    return data !== null ? toCamel(data) : null;
  }

  // Handle error response 422 with error data
  if (status === 422) {
    throw toCamel(data);
  }

  // Business Rule Violation (from existing Timelog API standards, to be upgraded in the future)
  if (status === 500 && data.Code === 102) {
    const errorResponse: TlpErrorResponse = toCamel(data);
    const violations: BusinessRuleViolation[] = errorResponse.details.map(
      (violation) => ({
        errorCode: violation.errorCode,
        violationDetails: violation.message,
      }),
    );

    throw new BusinessRuleViolationError(
      'A business rule was violated.',
      violations,
    );
  }

  throw Error(`${url} Status: ${status} (${statusText})`);
};

const responseXMLHandling = async (response: Response) => {
  const { status, statusText, url } = response;
  const data = await response.text();
  const parsedText = await new window.DOMParser().parseFromString(
    data,
    'text/xml',
  );
  if (response.ok) {
    return parsedText || '';
  }

  throw Error(`${url} Status: ${status} (${statusText})`);
};

const getMSWStatus = (key: string) => {
  const mswStatus = JSON.parse(Cookies.get(MSW_STATUS) || '{}');

  return mswStatus?.[key] ? `status=${mswStatus?.[key]}` : '';
};

export const generateUrl = ({ path, key, itemId = '' }: GenerateUrlProps) => {
  const customStatusParam = getMSWStatus(key);
  const fullUrl = tlpBase() + path + itemId;
  const joinQuery = fullUrl.indexOf('?') > 0 ? '&' : '?';

  return `${fullUrl}${customStatusParam ? joinQuery : ''}${customStatusParam}`;
};

export const responseHandlingForTest = responseHandling;
export const getMSWStatusForTest = getMSWStatus;
export const generateUrlForTest = generateUrl;

export const getFetch = async ({
  path,
  itemId,
  key,
  responseType = 'json',
}: GetFetchProps) => {
  const apiUrl = generateUrl({ path, itemId, key });
  const response = await fetchWithAuth(apiUrl, {
    method: 'GET',
    credentials: 'include',
  });

  if (responseType === 'xml') return responseXMLHandling(response);

  return responseHandling(response);
};

export const postFetch = async ({
  path,
  key,
  body,
  ...restProps
}: PostFetchProps) => {
  const response = await fetchWithAuth(generateUrl({ path, key }), {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body,
    ...restProps,
  });

  return responseHandling(response);
};

export const putFetch = async ({
  path,
  itemId,
  key,
  body,
  ...restProps
}: PutFetchProps) => {
  const response = await fetchWithAuth(generateUrl({ path, itemId, key }), {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body,
    ...restProps,
  });

  return responseHandling(response);
};

export const patchFetch = async ({
  path,
  key,
  body,
  ...restProps
}: PatchFetchProps) => {
  const response = await fetchWithAuth(generateUrl({ path, key }), {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body,
    ...restProps,
  });

  return responseHandling(response);
};

export const deleteFetch = async ({
  path,
  key,
  body,
  itemId,
}: DeleteFetchProps) => {
  const response = await fetchWithAuth(generateUrl({ path, key, itemId }), {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
  });

  return responseHandling(response);
};
